/**
 * PayslipsTab — Lista de liquidaciones con modal de detalle y descarga PDF.
 */
import { ordenControlado } from '@/components/ds/ordenControlado'
import { sortStateToParam, type SortState } from '@/components/SortableHeader'
import { useState, useRef } from 'react'
import { Search, Eye, Printer, Loader2, FolderArchive, FileArchive } from 'lucide-react'
import { useVisorPdf } from '@/components/VisorPdf'
import { usePayslips, usePayslip, usePeriods } from '../hooks/usePayroll'
import { useTenantLocale, formatNumber, formatDate } from '@/lib/locale'
import { usePermission } from '@/hooks/usePermission'
import apiClient, { apiDownload } from '@/lib/apiClient'
import { StatusBadge } from '../../../components/ds/StatusBadge'
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { PAYSLIP_STATUS } from '../../../components/ds/statusCatalogs'
import { Pagination } from '@/components/ds/Pagination'
import { Modal } from '@/components/ds/Modal'


const MONTHS = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic']
const MONTHS_FULL = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']

export default function PayslipsTab() {
  const [page, setPage] = useState(1)
  // El orden va al SERVIDOR (`?ordering=`): esta lista la pagina el backend,
  // y ordenar en el navegador ordenaría solo la página visible.
  const [orden, setOrden] = useState<SortState | null>(null)
  const [search, setSearch] = useState('')
  const [periodFilter, setPeriodFilter] = useState('')
  const [selectedId, setSelectedId] = useState('')
  const [descargandoLote, setDescargandoLote] = useState(false)
  const [errorLote, setErrorLote] = useState<string | null>(null)
  const locale = useTenantLocale()

  const { data: periodsData } = usePeriods()
  const { data, isLoading } = usePayslips({ ordering: sortStateToParam(orden), period_id: periodFilter || undefined, page, page_size: 20 })

  const payslips = data?.payslips || []
  const total = data?.count || 0
  const periods = periodsData?.periods || []

  const fmt = (v: string | number) => {
    const n = typeof v === 'string' ? parseFloat(v) : v
    return `$${formatNumber(n, locale, n % 1 !== 0 ? 2 : 0)}`
  }

  const periodoSeleccionado = periods.find(p => p.id === periodFilter)

  const descargarLote = async () => {
    if (!periodoSeleccionado) return
    const { year, month } = periodoSeleccionado
    const mm = String(month).padStart(2, '0')
    setErrorLote(null)
    setDescargandoLote(true)
    try {
      // `formato=zip`: sin él el servidor responde el PDF único (el de la pestaña
      // Períodos) y se guardaba un PDF con extensión .zip. Tarda lo mismo que ese
      // PDF, así que lleva su mismo tope (el de gunicorn) y no los 30 s del cliente.
      await apiDownload(`/payroll/periods/${periodoSeleccionado.id}/payslips-pdf/?formato=zip`,
                        `liquidaciones_${year}_${mm}.zip`, { timeout: 120_000 })
    } catch {
      // Un ZIP de 30 PDF puede tardar; si falla hay que decirlo, o el usuario
      // vuelve a apretar el botón sin saber si la primera vez pasó algo.
      setErrorLote('No se pudo generar el archivo. Vuelve a intentarlo; si el período '
                   + 'es grande puede tardar un momento.')
    } finally {
      setDescargandoLote(false)
    }
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle" />
          <input aria-label="Buscar empleado" type="text" placeholder="Buscar empleado..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(1) }}
            className="w-full pl-9 pr-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200" />
        </div>
        <select aria-label="Todos los períodos" value={periodFilter} onChange={e => { setPeriodFilter(e.target.value); setPage(1) }}
          className="px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content">
          <option value="">Todos los períodos</option>
          {periods.map(p => (
            <option key={p.id} value={p.id}>{MONTHS[(p.month || 1) - 1]} {p.year}</option>
          ))}
        </select>
        <span className="text-sm text-content-muted">{total} liquidaciones</span>

        {/* Descargar todas las del mes en un ZIP. El endpoint existía desde antes y
            vivía solo en la pestaña Períodos, como uno de cuatro íconos grises sin
            etiqueta — RRHH pedía la descarga por lote sin saber que ya estaba. Acá
            es donde la busca: la pantalla donde ve las liquidaciones. */}
        <button type="button" onClick={descargarLote}
          disabled={!periodoSeleccionado || descargandoLote || total === 0}
          title={periodoSeleccionado
            ? 'Descarga un PDF por liquidación, en un archivo .zip'
            : 'Elegir un período: el ZIP se arma con las liquidaciones de un mes'}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-divider rounded-lg text-content-muted hover:bg-soft disabled:opacity-40 disabled:cursor-not-allowed">
          {descargandoLote ? <Loader2 size={14} className="animate-spin" /> : <FileArchive size={14} />}
          {descargandoLote ? 'Generando…' : 'Descargar todas (.zip)'}
        </button>
      </div>
      {errorLote && <p className="mb-4 text-sm text-danger-solid">{errorLote}</p>}

      <DataTable<any>
            {...ordenControlado(orden, s => { setOrden(s); setPage(1) })}
        columns={[
          { key: 'employee_name', header: 'Empleado', sortable: true,
            render: ps => <span className="font-medium text-content">{ps.employee_name}</span> },
          { key: 'employee_rut', header: 'RUT', sortable: true,
            render: ps => <span className="text-xs text-content-subtle font-mono">{ps.employee_rut}</span> },
          { key: 'gross_salary', header: 'Bruto', numeric: true, sortable: true,
            sortValue: ps => Number(ps.gross_salary), render: ps => fmt(ps.gross_salary) },
          { key: 'total_deductions', header: 'Descuentos', numeric: true, sortable: true,
            sortValue: ps => Number(ps.total_deductions),
            render: ps => <span className="text-danger-solid">{fmt(ps.total_deductions)}</span> },
          { key: 'net_salary', header: 'Líquido', numeric: true, sortable: true,
            sortValue: ps => Number(ps.net_salary),
            render: ps => <span className="font-bold text-primary">{fmt(ps.net_salary)}</span> },
          { key: 'status', header: 'Estado', sortable: true,
            render: ps => <StatusBadge status={ps.status} catalog={PAYSLIP_STATUS} /> },
        ]}
        rows={payslips}
        rowKey={ps => ps.id}
        minWidth={780}
        loading={isLoading}
        onRowClick={ps => setSelectedId(ps.id)}
        empty={<EmptyState title="Sin liquidaciones." size="sm" />}
        caption="Liquidaciones de sueldo del período"
      />

      {total > 20 && (
        <Pagination
          page={page}
          pageSize={20}
          total={total}
          onPageChange={setPage}
          locale={locale}
        />
      )}

      {selectedId && (
        <PayslipModal payslipId={selectedId} onClose={() => setSelectedId('')} fmt={fmt} />
      )}
    </>
  )
}

// ─── Payslip Modal ──────────────────────────────────────────────────────────

export function PayslipModal({ payslipId, onClose, fmt }: { payslipId: string; onClose: () => void; fmt: (v: string | number) => string }) {
  const locale = useTenantLocale()
  const { data: ps, isLoading } = usePayslip(payslipId)
  const contentRef = useRef<HTMLDivElement>(null)
  const { ver, visor } = useVisorPdf()
  const { can } = usePermission()


  // Se ve en el visor y desde ahí se descarga, con este nombre.
  const verPdf = () => {
    if (!ps) return
    const rut = (ps.employee_rut as string || '').replace(/\./g, '').replace('-', '_')
    const periodLabel = (ps.period_label as string || '').replace('-', '_')
    ver({
      titulo: `Liquidación ${ps.period_label ?? ''} · ${ps.employee_name ?? ''}`,
      nombreArchivo: `liquidacion_${rut}_${periodLabel}.pdf`,
      cargar: async () => (await apiClient.get(`/payroll/payslips/${ps.id}/pdf/`, { responseType: 'blob' })).data,
    })
  }

  const [materializing, setMaterializing] = useState(false)
  const handleMaterializeDocument = async () => {
    if (!ps || materializing) return
    setMaterializing(true)
    try {
      await apiClient.post(`/payroll/payslips/${ps.id}/materialize-document/`)
      // eslint-disable-next-line no-alert
      alert('Documento generado y enviado a la carpeta del trabajador.')
    } catch (exc) {
      // eslint-disable-next-line no-console
      console.error('Error materializando documento', exc)
      // eslint-disable-next-line no-alert
      alert('No se pudo generar el documento. Revisa los logs.')
    } finally {
      setMaterializing(false)
    }
  }

  const handlePrint = () => {
    if (!contentRef.current || !ps) return
    const printWindow = window.open('', '_blank', 'width=800,height=900')
    if (!printWindow) return

    // Copiar los stylesheets de la app (Tailwind compilado)
    const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map(el => el.outerHTML).join('\n')

    printWindow.document.write(`
      <!DOCTYPE html><html><head><title>Liquidación — ${ps.employee_name}</title>
      ${styles}
      <style>
        @page { margin: 12mm; }
        body { background: white !important; padding: 0 !important; }
        .print-footer { margin-top: 48px; padding-top: 16px; border-top: 1px solid #e2e8f0;
          font-size: 11px; color: #94a3b8; text-align: center; }
        .print-signature { margin-top: 48px; text-align: center; font-size: 12px; color: #64748b; }
        .print-signature .line { display: inline-block; width: 200px; border-bottom: 1px solid #94a3b8;
          margin-bottom: 4px; }
      </style></head><body>
      <div style="max-width: 720px; margin: 0 auto; padding: 16px;">
        ${contentRef.current.innerHTML}
        <div class="print-signature">
          <div class="line">&nbsp;</div><br>Firma del Trabajador
        </div>
        <div class="print-footer">
          Certifico que he recibido de <strong>${(ps as any).company_info?.legal_name || ''} (${(ps as any).company_info?.tax_id || ''})</strong> a mi entera satisfacción el saldo indicado en la presente Liquidación y no tengo cargo ni cobro posterior que hacer.
          <br><br>Documento generado por Clobi ERP — ${new Date().toLocaleDateString('es-CL')}
        </div>
      </div>
      <script>setTimeout(function(){window.print()},500)</script>
      </body></html>
    `)
    printWindow.document.close()
  }

  if (!payslipId) return null

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title="Liquidación de Sueldo"
      actions={
        <>
          {can('payroll.payslips', 'view') && (
            <button
              onClick={verPdf}
              disabled={!ps}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-primary border border-primary-100 rounded-lg hover:bg-primary-50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              title="Ver el PDF y descargarlo"
            >
              <Eye size={12} aria-hidden="true" />
              Ver PDF
            </button>
          )}
          {can('payroll.payslips', 'approve') && ps && (ps.status === 'approved' || ps.status === 'paid') && (
            <button
              onClick={handleMaterializeDocument}
              disabled={materializing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-content-muted border border-divider rounded-lg hover:bg-surface-muted disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              title="Genera el PDF y lo envía a la carpeta digital del trabajador"
            >
              {materializing ? <Loader2 size={12} className="animate-spin" /> : <FolderArchive size={12} />}
              {materializing ? 'Generando...' : 'Enviar a carpeta'}
            </button>
          )}
          <button onClick={handlePrint} disabled={!ps}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-content-muted border border-divider rounded-lg hover:bg-surface-muted">
            <Printer size={12} /> Imprimir
          </button>
        </>
      }
    >
      <div>
        {isLoading ? (
          <p className="text-center text-sm text-content-subtle py-8">Cargando...</p>
        ) : !ps ? (
          <p className="text-center text-sm text-content-subtle py-8">Liquidación no encontrada.</p>
        ) : (
          <div ref={contentRef}>
            {/* Header: Empresa + Título + Período */}
            <div className="flex items-start justify-between border-b-2 border-primary pb-4 mb-4">
              <div>
                <h1 className="text-lg font-bold text-content">Liquidación de Sueldo</h1>
                <p className="text-sm text-content-muted">
                  Mes: {ps.period_label ? `${MONTHS_FULL[parseInt(ps.period_label.split('-')[1]) - 1] || ''} ${ps.period_label.split('-')[0]}` : ''}
                </p>
              </div>
              {(ps as any).company_info?.legal_name && (
                <div className="text-right">
                  <p className="text-sm font-bold text-content">{(ps as any).company_info.legal_name}</p>
                  <p className="text-xs text-content-muted font-mono">{(ps as any).company_info.tax_id}</p>
                </div>
              )}
            </div>

            {/* Datos del empleado */}
            {(() => {
              const info = (ps as any).employee_info || {}
              return (
                <div className="grid grid-cols-3 gap-x-6 gap-y-1.5 text-xs mb-4 p-3 bg-glass-3 rounded-lg border border-divider">
                  <div><span className="text-content-subtle">Empleado: </span><span className="font-semibold text-content">{ps.employee_name}</span></div>
                  <div><span className="text-content-subtle">RUT: </span><span className="font-semibold text-content font-mono">{ps.employee_rut}</span></div>
                  {info.position && <div><span className="text-content-subtle">Cargo: </span><span className="font-medium text-content">{info.position}</span></div>}
                  {info.contract_type && <div><span className="text-content-subtle">Contrato: </span><span className="font-medium text-content">{info.contract_type}</span></div>}
                  {info.hire_date && <div><span className="text-content-subtle">Ingreso: </span><span className="font-medium text-content">{formatDate(info.hire_date, locale)}</span></div>}
                  <div><span className="text-content-subtle">Sueldo Base: </span><span className="font-semibold text-content">{fmt(ps.base_salary)}</span></div>
                  {info.afp && <div><span className="text-content-subtle">Previsión: </span><span className="font-medium text-content">{info.afp}</span></div>}
                  {info.health_system && (
                    <div><span className="text-content-subtle">Salud: </span><span className="font-medium text-content">
                      {info.isapre_name ? `${info.isapre_name} ${info.isapre_plan_uf} UF` : info.health_system}
                    </span></div>
                  )}
                  {info.work_days && <div><span className="text-content-subtle">Días Trabajados: </span><span className="font-medium text-content">{info.work_days} días</span></div>}
                  {info.apv_info && <div><span className="text-content-subtle">APV: </span><span className="font-medium text-content">{info.apv_info}</span></div>}
                </div>
              )
            })()}

            {/* Líquido destacado */}
            <div className="bg-primary-50 border border-primary-100 rounded-xl p-4 text-center mb-5">
              <p className="text-xs text-content-muted uppercase tracking-wider mb-1">Líquido a Pagar</p>
              <p className="text-3xl font-black text-primary">{fmt(ps.net_salary)}</p>
            </div>

            {/* Ausencias del período */}
            {(ps.extra_sections?.absences || []).length > 0 && (
              <div className="mb-5 p-4 rounded-xl border border-warning-border bg-amber-50/60 dark:bg-amber-900/20 text-sm">
                <h3 className="text-xs font-bold text-warning-content uppercase tracking-wider mb-2">
                  Ausencias del período
                </h3>
                <ul className="space-y-1">
                  {(ps.extra_sections?.absences as any[]).map((a, i: number) => (
                    <li key={i} className="flex items-center justify-between gap-3">
                      <span className="text-content">
                        {a.leave_type_display} · {a.start_date} → {a.end_date}
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="font-mono text-xs text-content-muted">{a.days} día{a.days === 1 ? '' : 's'}</span>
                        <span className={`text-[10px] inline-flex px-2 py-0.5 rounded-full ${a.paid ? 'bg-success-surface-strong text-success-content' : 'bg-danger-surface-strong text-danger-content'}`}>
                          {a.paid ? 'Con goce' : 'Sin goce / subsidio'}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Saldo vacaciones */}
            {ps.vacation_balance && (
              <div className="mb-5 flex items-center justify-between px-4 py-3 rounded-xl border border-divider bg-surface text-sm">
                <div>
                  <span className="text-content-muted">Saldo de vacaciones al período: </span>
                  <span className="font-bold text-content">
                    {Number(ps.vacation_balance.days_pending).toFixed(1)} días
                  </span>
                  {ps.vacation_balance.is_progressive && (
                    <span className="ml-2 text-[10px] inline-flex px-2 py-0.5 rounded-full bg-warning-surface-strong text-warning-content">
                      Progresivo (≥13 años)
                    </span>
                  )}
                </div>
                <div className="text-xs text-content-subtle">
                  {ps.vacation_balance.days_entitled_per_year} días/año · {ps.vacation_balance.years_of_service} años de servicio
                </div>
              </div>
            )}

            {/* Tablas de haberes y descuentos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Haberes */}
              <div className="border border-success-border rounded-xl overflow-hidden">
                <div className="bg-success-surface px-4 py-2 border-b border-success-border">
                  <h3 className="text-xs font-bold text-success-content uppercase tracking-wider">Haberes</h3>
                </div>
                <div className="px-4 py-3 space-y-2">
                  <LiqRow label="Sueldo Base" value={fmt(ps.base_salary)} />
                  <LiqRow label="Gratificación Legal" value={fmt(ps.gratification)} />
                  {parseFloat(ps.overtime_hours) > 0 && (
                    <LiqRow label={`Horas Extra 50% (${ps.overtime_hours}h)`} value={fmt(ps.overtime_amount)} />
                  )}
                  {parseFloat(ps.overtime_holiday_hours || '0') > 0 && (
                    <LiqRow label={`HE Domingo/Festivo 100% (${ps.overtime_holiday_hours}h)`} value={fmt(ps.overtime_holiday_amount || '0')} />
                  )}
                  {/* Un bono prorrateado se dice: si el trabajador tiene pactados
                      $30.000 de teletrabajo y le llegan $26.000, la liquidación
                      tiene que explicar de dónde sale la diferencia. Sin esto es
                      un descuento invisible, el problema del #1059 por otra vía. */}
                  {(ps.bonuses || []).map((b: any, i: number) => (
                    <LiqRow key={i}
                      label={b.prorated
                        ? `${b.name} (${ps.days_worked}/30 de ${fmt(b.amount_full)})`
                        : b.name}
                      value={fmt(b.amount)}
                      badge={!b.is_imponible ? 'No imp.' : undefined} />
                  ))}
                  {/* 🔴 La asignación familiar YA está sumada en `gross_salary`
                      (calculation.py:357), así que sin esta línea el desglose no
                      cuadra: el TOTAL HABERES sale mayor que la suma de lo que se
                      ve, sin nada que lo explique. Reportado el 2026-08-25 —
                      Maverick González tenía $4.382 de asignación familiar
                      invisibles en pantalla, mientras el PDF sí la imprimía
                      (pdf_service.py:149). Es un haber NO imponible y NO
                      tributable: es un beneficio del Estado, no remuneración. */}
                  {parseFloat(ps.family_allowance || '0') > 0 && (
                    <LiqRow
                      label={(() => {
                        const n = Number(
                          (ps.calculation_details?.family_allowance?.dependents) ?? 0)
                        return n > 0
                          ? `Asignación Familiar (${n} carga${n !== 1 ? 's' : ''})`
                          : 'Asignación Familiar'
                      })()}
                      value={fmt(ps.family_allowance || '0')}
                      badge="No imp." />
                  )}
                  <div className="border-t border-success-border pt-2 mt-2">
                    <LiqRow label="TOTAL HABERES" value={fmt(ps.gross_salary)} bold />
                  </div>
                  <LiqRow label="Imponible" value={fmt(ps.total_imponible)} muted />
                  <LiqRow label="Base Tributable" value={fmt(ps.total_tributable)} muted />
                </div>
              </div>

              {/* Descuentos: legales (cotizaciones obligatorias + impuesto) y otros
                  (préstamos, créditos CCAF, descuentos voluntarios) en secciones separadas. */}
              {(() => {
                const otrosList = (ps.other_deductions || []) as any[]
                const otrosTotal = otrosList.reduce((s: number, d: any) => s + (parseFloat(d.amount) || 0), 0)
                const legalTotal = (parseFloat(ps.total_deductions) || 0) - otrosTotal
                // La cotización CCAF (cuando la empresa está afiliada) sí es un descuento
                // previsional; las cuotas de crédito CCAF van en "Otros descuentos".
                const ccafCotiz = ps.extra_sections?.ccaf?.configured ? ps.extra_sections.ccaf : null
                return (
                  <div className="space-y-5">
                    {/* Descuentos legales */}
                    <div className="border border-danger-border rounded-xl overflow-hidden">
                      <div className="bg-danger-surface px-4 py-2 border-b border-danger-border">
                        <h3 className="text-xs font-bold text-danger-content-soft uppercase tracking-wider">Descuentos Legales</h3>
                      </div>
                      <div className="px-4 py-3 space-y-2">
                        <LiqRow label="Cotiz. Prev. Obligatoria (AFP)" value={fmt(ps.afp_employee)} />
                        <LiqRow label="Cotiz. Salud Obligatoria" value={fmt(ps.health_employee)} />
                        {parseFloat(ps.health_additional) > 0 && (
                          <LiqRow label="Adicional Salud (Isapre)" value={fmt(ps.health_additional)} />
                        )}
                        <LiqRow label="Seguro de Cesantía" value={fmt(ps.unemployment_employee)} />
                        <LiqRow label="Impuesto Único" value={fmt(ps.tax_amount)} />
                        {parseFloat(ps.apv_amount) > 0 ? (
                          <LiqRow label={`APV${ps.extra_sections?.apv?.regime ? ` (${ps.extra_sections.apv.regime})` : ''}`} value={fmt(ps.apv_amount)} />
                        ) : ps.extra_sections?.apv?.configured ? (
                          <LiqRow label="APV" value={fmt(0)} muted />
                        ) : null}
                        {ccafCotiz && (
                          <LiqRow label="Cotización CCAF" value={fmt(ccafCotiz.amount)} />
                        )}
                        {/* 🔴 Cuenta 2 y Depósito Convenido SE DESCUENTAN del líquido
                            (`calculation.py:524-528`) y no se mostraban ni acá ni en el
                            PDF. Auditado el 2026-08-25: 9 liquidaciones afectadas —
                            Lissette Gutiérrez con $50.000 al mes durante 8 meses, sin
                            nada en su liquidación que lo explicara. Son ahorro
                            voluntario del trabajador, así que tiene todo el derecho a
                            verlos. */}
                        {parseFloat(ps.cuenta2_amount || '0') > 0 && (
                          <LiqRow label="Cuenta 2 / Ahorro Voluntario"
                                  value={fmt(ps.cuenta2_amount)} />
                        )}
                        {parseFloat(ps.deposito_convenido_amount || '0') > 0 && (
                          <LiqRow label="Depósito Convenido"
                                  value={fmt(ps.deposito_convenido_amount)} />
                        )}
                        <div className="border-t border-danger-border pt-2 mt-2">
                          <LiqRow label="Total Descuentos Legales" value={fmt(legalTotal)} bold red />
                        </div>
                      </div>
                    </div>

                    {/* Otros descuentos (no legales) */}
                    {otrosList.length > 0 && (
                      <div className="border border-warning-border rounded-xl overflow-hidden">
                        <div className="bg-warning-surface px-4 py-2 border-b border-warning-border">
                          <h3 className="text-xs font-bold text-warning-content uppercase tracking-wider">Otros Descuentos</h3>
                        </div>
                        <div className="px-4 py-3 space-y-2">
                          {otrosList.map((d: any, i: number) => (
                            <LiqRow key={i} label={d.name} value={fmt(d.amount)} />
                          ))}
                          <div className="border-t border-warning-border pt-2 mt-2">
                            <LiqRow label="Total Otros Descuentos" value={fmt(otrosTotal)} bold />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })()}

            </div>

            {/* Totales resumen */}
            <div className="mt-5 grid grid-cols-3 gap-3 text-center">
              <div className="p-3 bg-glass-3 rounded-lg border border-divider">
                <p className="text-[10px] text-content-subtle uppercase">Total Haberes</p>
                <p className="text-sm font-bold text-content">{fmt(ps.gross_salary)}</p>
              </div>
              <div className="p-3 bg-danger-surface rounded-lg border border-danger-border">
                <p className="text-[10px] text-danger-solid uppercase">Total Descuentos</p>
                <p className="text-sm font-bold text-danger-content-soft">{fmt(ps.total_deductions)}</p>
              </div>
              {Number(ps.sobregiro || 0) > 0 && (
                // Los descuentos superaron los haberes: el líquido queda en 0 y la
                // empresa absorbe la diferencia (no se arrastra al mes siguiente).
                <div className="col-span-3 p-3 bg-warning-surface rounded-lg border border-warning-border text-left">
                  <p className="text-[10px] text-warning-content uppercase">Sobregiro absorbido por la empresa</p>
                  <p className="text-sm font-bold text-warning-content-strong">{fmt(ps.sobregiro!)}</p>
                </div>
              )}
              <div className="p-3 bg-primary-50 rounded-lg border border-primary-100">
                <p className="text-[10px] text-primary uppercase">Líquido a Recibir</p>
                <p className="text-sm font-bold text-primary">{fmt(ps.net_salary)}</p>
              </div>
            </div>
          </div>
        )}
      </div>
      {visor}
    </Modal>
  )
}

function LiqRow({ label, value, bold, red, muted, badge }: {
  label: string; value: string; bold?: boolean; red?: boolean; muted?: boolean; badge?: string
}) {
  return (
    <div className={`flex items-center justify-between ${bold ? 'font-bold border-t border-divider pt-2 mt-1' : ''}`}>
      <span className={`text-xs ${muted ? 'text-content-subtle' : 'text-content-muted'}`}>
        {label}
        {badge && <span className="ml-1 text-[9px] text-content-subtle bg-surface-muted px-1 py-0.5 rounded">{badge}</span>}
      </span>
      <span className={`text-xs tabular-nums ${bold ? 'font-bold' : 'font-medium'} ${red ? 'text-danger-content-soft' : muted ? 'text-content-subtle' : 'text-content'}`}>
        {value}
      </span>
    </div>
  )
}
